<template>
	<div class="user-center-container invoice-selection-page">
		<div class="user-center-content invoice-selection-content">
			<div class="invoice-page-header">
				<div>
					<h2>申请开票</h2>
					<p>选择已支付且尚未申请发票的订单，填写开票信息</p>
				</div>
			</div>

			<div class="invoice-order-panel">
				<div class="invoice-panel-heading">
					<div>
						<strong>可开票订单</strong>
						<span>共 {{ pageOptions.total }} 条订单记录</span>
					</div>
					<span class="electronic-invoice-tag">
						<i class="el-icon-message"></i>
						电子发票
					</span>
				</div>

				<!-- 查询待开发票的订单 -->
				<data-table
					ref="invoiceOrderTable"
					class="invoice-order-table"
					:table-data="orderList"
					:column="columns"
					:page-options="pageOptions"
					:border="true"
					:stripe="false"
					:loading="loading"
					:selection="true"
					:reserve-selection="true"
					:row-keys="handleRowKeys"
					empty-text="暂无可开票订单"
					@selectionChange="handleSelectDataChange"
					@page-change="handlePaginationChange"
				>
					<template slot="orderCode" slot-scope="{ scope }">
						<div class="order-code-cell">
							<i class="el-icon-tickets"></i>
							<span>{{ scope.row.orderCode }}</span>
						</div>
					</template>
					<template slot="productName" slot-scope="{ scope }">
						<div class="product-name-cell">
							<strong>{{ getProductName(scope.row) }}</strong>
							<span>{{ getOrderCategoryName(scope.row) }}</span>
						</div>
					</template>
					<template slot="payTime" slot-scope="{ scope }">
						<div class="order-time-cell">
							<i class="el-icon-time"></i>
							<span>{{ scope.row.payTime || scope.row.createTime || '-' }}</span>
						</div>
					</template>
					<template slot="actualPayment" slot-scope="{ scope }">
						<div class="invoice-amount-cell">
							<span>¥</span>
							<strong>{{ formatAmount(scope.row.actualPayment) }}</strong>
						</div>
					</template>
				</data-table>
			</div>

			<!-- 底部操作栏 -->
			<div class="invoice-footer">
				<div class="selected-info">
					<span>
						已选择
						<em>{{ selectedCount }}</em>
						笔订单，合计：
					</span>
					<span class="total-amount">¥{{ totalAmount.toFixed(2) }}</span>
				</div>
				<el-button type="primary" icon="el-icon-document-checked" :disabled="selectedCount === 0" @click="handleApplyInvoice">
					申请开票
				</el-button>
			</div>
		</div>

		<!-- 发票信息填写弹窗 -->
		<jr-dialog
			:visible.sync="editInvoiceVisible"
			:show-btn="false"
			width="720px"
			top="8vh"
			custom-class="invoice-apply-dialog"
			@close="handleCloseEditInvoice"
		>
			<template slot="title">
				<div class="invoice-dialog-title">
					<span class="invoice-dialog-title__icon"><i class="el-icon-document-checked"></i></span>
					<div>
						<h4>申请电子发票</h4>
						<p>
							<span>{{ selectedCount }} 笔订单</span>
							<i></i>
							<strong>¥{{ totalAmount.toFixed(2) }}</strong>
						</p>
					</div>
				</div>
			</template>
			<template slot="content">
				<div class="form-content invoice-dialog-content">
					<div class="invoice-title-toolbar">
						<h5>选择发票抬头</h5>
						<el-button type="text" icon="el-icon-setting" @click="handleManageInvoiceTitle">管理抬头</el-button>
					</div>

					<el-form ref="invoiceForm" :model="invoiceForm" :rules="rules">
						<el-form-item prop="invoiceTitleId" class="form-item invoice-title-form-item">
							<el-select v-model="invoiceForm.invoiceTitleId" placeholder="请选择发票抬头">
								<el-option v-for="title in invoiceTitleList" :key="title.id" :label="title.taxpayer" :value="title.id">
									<span class="title-option-name">{{ title.taxpayer }}</span>
									<span class="title-option-type">
										{{ title.headType === '1' ? '个人' : '企业' }}{{ title.isDefault ? ' · 默认' : '' }}
									</span>
								</el-option>
							</el-select>
						</el-form-item>

						<div v-if="selectedInvoiceTitle" class="invoice-title-preview">
							<div class="preview-heading">
								<div>
									<i class="el-icon-office-building"></i>
									<span>抬头信息</span>
								</div>
								<div class="preview-tags">
									<span>{{ selectedInvoiceTitle.invoiceType === '1' ? '专用发票' : '普通发票' }}</span>
									<span>{{ selectedInvoiceTitle.headType === '1' ? '个人' : '企业' }}</span>
								</div>
							</div>
							<div class="preview-grid">
								<div class="preview-row preview-row--wide">
									<span>发票抬头</span>
									<strong>{{ selectedInvoiceTitle.taxpayer }}</strong>
								</div>
								<div v-if="selectedInvoiceTitle.headType === '0'" class="preview-row preview-row--wide">
									<span>纳税人识别号</span>
									<strong>{{ selectedInvoiceTitle.taxpayerCode }}</strong>
								</div>
								<div v-if="selectedInvoiceTitle.companyAddr" class="preview-row preview-row--wide">
									<span>注册地址</span>
									<strong>{{ selectedInvoiceTitle.companyAddr }}</strong>
								</div>
								<div v-if="selectedInvoiceTitle.companyPhone" class="preview-row">
									<span>注册电话</span>
									<strong>{{ selectedInvoiceTitle.companyPhone }}</strong>
								</div>
								<div v-if="selectedInvoiceTitle.bankName" class="preview-row">
									<span>开户银行</span>
									<strong>{{ selectedInvoiceTitle.bankName }}</strong>
								</div>
								<div v-if="selectedInvoiceTitle.bankCode" class="preview-row preview-row--wide">
									<span>银行账号</span>
									<strong>{{ selectedInvoiceTitle.bankCode }}</strong>
								</div>
							</div>
						</div>

						<div class="form-tips">
							<i class="el-icon-info"></i>
							<p>
								提示：请按照电子商务法及发票管理办法等相关规定填写发票信息，若因填写无效信息或非法信息等造成包括但不限于无法成功开具发票等后果或法律责任，由您自行承担。
							</p>
						</div>
					</el-form>

					<div class="form-button-group">
						<el-button :disabled="submitLoading" class="form-button form-button--default" @click="handleCloseEditInvoice">
							取消
						</el-button>
						<el-button
							:loading="submitLoading"
							type="primary"
							class="form-button form-button--primary"
							@click="handleSubmitInvoiceInfo"
						>
							提交申请
						</el-button>
					</div>
				</div>
			</template>
		</jr-dialog>
	</div>
</template>

<script>
	import DataTable from '@/components/JrTable/DataTable.vue'
	import JrDialog from '@/components/Dialog'
	import { queryOrderRecordInvoice, saveApplyInvoiceInfo } from '@/api/memberBenefit/index'
	import { queryInvoiceTitleList } from '@/api/invoice'

	// 申请开票
	export default {
		name: 'OrderSelect',
		components: {
			DataTable,
			JrDialog
		},
		props: {},
		data() {
			return {
				queryParams: {
					orderId: this.$route.query.orderId || ''
				},
				// 加载状态
				loading: false,
				columns: [
					{ label: '订单编号', prop: 'orderCode', slot: 'orderCode', minWidth: '230', align: 'left' },
					{ label: '商品名称', prop: 'productName', slot: 'productName', minWidth: '260', align: 'left' },
					{ label: '支付时间', prop: 'payTime', slot: 'payTime', width: '220', align: 'left' },
					{ label: '实付金额', prop: 'actualPayment', slot: 'actualPayment', width: '150', align: 'right' }
				],
				// 订单列表
				orderList: [],
				// 分页配置
				pageOptions: {
					limit: 10,
					page: 1,
					total: 0
				},
				// 非多个禁用
				multiple: true,
				// 选中的订单数据
				selectedOrders: [],
				// 所选中的订单ID集合
				selectedOrderIds: [],
				// 已选择的订单金额
				totalAmount: 0,
				// 发票填写弹窗
				editInvoiceVisible: false,
				// 提交加载状态
				submitLoading: false,
				// 发票抬头信息
				invoiceForm: {
					invoiceTitleId: ''
				},
				invoiceTitleList: [],
				rules: {
					invoiceTitleId: [{ required: true, message: '请选择发票抬头', trigger: 'change' }]
				}
			}
		},
		computed: {
			// 已选择的订单数量
			selectedCount() {
				return this.selectedOrderIds.length
			},
			selectedInvoiceTitle() {
				return this.invoiceTitleList.find(item => item.id === this.invoiceForm.invoiceTitleId)
			}
		},
		watch: {},
		created() {},
		mounted() {
			this.getOrderList()
		},
		methods: {
			getProductName(order) {
				return order.productName || order.eventName || order.setMealName || '未命名商品'
			},
			getOrderCategoryName(order) {
				const categoryMap = {
					MERCHANT_SELF_GOODS: '自营商品',
					MERCHANT_EXHIBITION_GOODS: '展销商品',
					MERCHANT_PUBLICITY: '企业宣传',
					MERCHANT_ELEGANCEANDCHARM: '企业风采'
				}
				return categoryMap[order.orderType] || (order.setMealName ? '会员商品' : '商城商品')
			},
			formatAmount(value) {
				const amount = Number(value)
				return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
			},
			// 获取订单列表
			async getOrderList() {
				this.loading = true
				try {
					const params = {
						page: this.pageOptions.page,
						size: this.pageOptions.limit,
						...this.queryParams
					}

					const { data } = await queryOrderRecordInvoice(params)

					if (data && data.records) {
						this.orderList = data.records
						this.pageOptions.total = data.total || 0

						// 数据加载完成后恢复选中状态
						if (this.selectedOrders.length) {
							this.orderList = data.records.map(item => {
								item._selected = this.selectedOrders.some(sel => sel.id === item.id)
								return item
							})
						}
						this.selectRequestedOrder()
					} else {
						this.orderList = []
						this.pageOptions.total = 0
					}
				} catch (error) {
					this.orderList = []
					this.pageOptions.total = 0
				} finally {
					this.loading = false
				}
			},
			selectRequestedOrder() {
				const orderId = this.queryParams.orderId
				if (!orderId) return
				const targetOrder = this.orderList.find(item => item.id === orderId)
				if (!targetOrder) return
				this.$nextTick(() => {
					const tableComponent = this.$refs.invoiceOrderTable
					const table = tableComponent && tableComponent.$refs.dataTable
					if (!table) return
					table.toggleRowSelection(targetOrder, true)
					if (this.$route.query.autoApply === '1') {
						this.$nextTick(() => this.handleApplyInvoice())
					}
				})
			},

			handleRowKeys(row) {
				return row.id
			},

			// 选中回调
			handleSelectDataChange(selection) {
				// 更新当前页的选中状态
				this.orderList.forEach(item => {
					item._selected = selection.some(sel => sel.id === item.id)
				})

				// 更新全局选中列表
				selection.forEach(item => {
					const index = this.selectedOrders.findIndex(sel => sel.id === item.id)
					if (index === -1) {
						// 添加新选中项
						this.selectedOrders.push({ ...item })
					} else {
						// 更新已存在项（可能数据有变化）
						this.selectedOrders[index] = { ...item }
					}
				})

				// 移除取消选中的项
				const currentPageIds = new Set(this.orderList.map(item => item.id))
				this.selectedOrders = this.selectedOrders.filter(
					sel => !currentPageIds.has(sel.id) || selection.some(item => item.id === sel.id)
				)

				// 计算总价
				this.totalAmount = this.selectedOrders.reduce((sum, order) => {
					return sum + (parseFloat(order.actualPayment) || 0)
				}, 0)

				this.selectedOrderIds = this.selectedOrders.map(item => item.id)
				this.multiple = !this.selectedOrderIds.length
			},

			// 分页变化
			async handlePaginationChange(pager) {
				const { page, limit } = pager
				this.pageOptions.page = page
				this.pageOptions.limit = limit

				// 记录当前页的选中状态
				this.orderList.forEach(item => {
					if (item._selected) {
						const existing = this.selectedOrders.find(sel => sel.id === item.id)
						if (!existing) {
							this.selectedOrders.push({ ...item })
						}
					}
				})

				await this.getOrderList()
			},

			// 申请开票
			async handleApplyInvoice() {
				if (this.selectedOrderIds.length === 0) {
					this.$message.warning('请至少选择一个订单')
					return
				}

				await this.handleQueryMyInvoiceHeadInfo()
				if (!this.invoiceTitleList.length) {
					this.$modal
						.confirm('尚未维护发票抬头，请先新增发票抬头。确认后前往抬头管理。')
						.then(() => this.handleManageInvoiceTitle())
						.catch(() => {})
					return
				}
				this.editInvoiceVisible = true
			},

			// 查询我的发票抬头信息
			async handleQueryMyInvoiceHeadInfo() {
				const { data } = await queryInvoiceTitleList()
				this.invoiceTitleList = Array.isArray(data) ? data : []
				const currentExists = this.invoiceTitleList.some(item => item.id === this.invoiceForm.invoiceTitleId)
				if (!currentExists) {
					const defaultTitle = this.invoiceTitleList.find(item => item.isDefault) || this.invoiceTitleList[0]
					this.invoiceForm.invoiceTitleId = defaultTitle ? defaultTitle.id : ''
				}
			},

			handleManageInvoiceTitle() {
				this.$router.push('/user/invoiceManagement/invoiceTitle')
			},

			// 关闭发票信息填写弹窗
			handleCloseEditInvoice() {
				this.editInvoiceVisible = false
			},

			// 申请开票信息提交
			handleSubmitInvoiceInfo() {
				this.$refs.invoiceForm.validate(valid => {
					if (valid) {
						this.submitLoading = true
						const params = {
							orderIds: this.selectedOrderIds,
							invoiceTitleId: this.invoiceForm.invoiceTitleId
						}

						saveApplyInvoiceInfo(params)
							.then(response => {
								this.submitLoading = false
								const count = Number(response.data || 0)
								this.$modal.msgSuccess(count > 1 ? `已按开票主体生成 ${count} 笔申请` : '申请开票成功')
								this.handleCloseEditInvoice()
								this.selectedOrders = []
								this.selectedOrderIds = []
								this.totalAmount = 0
								this.multiple = true
								// 再次查询，刷新待开发票的订单信息
								this.getOrderList()
							})
							.catch(() => {
								this.submitLoading = false
							})
					}
				})
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import '~@/views/user/styles/user-center.scss';
	@import '~@/styles/element-reset.scss';

	.invoice-selection-content {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #fff;
		box-sizing: border-box;
	}

	.invoice-page-header {
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 21px;
			font-weight: 600;
		}

		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.invoice-order-panel {
		margin-top: 16px;
		padding: 18px;
		border: 1px solid #e4e9ef;
		border-radius: 7px;
		background: #fff;
	}

	.invoice-panel-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 15px;

		strong {
			color: #303b4c;
			font-size: 17px;
			font-weight: 600;
		}

		div > span {
			margin-left: 12px;
			color: #969faa;
			font-size: 13px;
		}

		.electronic-invoice-tag {
			padding: 6px 10px;
			border: 1px solid #b9decf;
			border-radius: 4px;
			background: #f0faf6;
			color: #278464;
			font-size: 12px;
		}
	}

	.invoice-order-table {
		width: 100%;

		::v-deep {
			.data-table {
				border-color: #e3eaf1;
			}

			.el-table,
			.el-table__expanded-cell {
				background: #fff;
			}

			.el-table::before,
			.el-table--border::after {
				background-color: #e3eaf1;
			}

			.el-table__header-wrapper th,
			.el-table__header-wrapper .default-th {
				height: 46px;
				padding: 0;
				border-color: #e3eaf1;
				background: #f2f5f8;
				color: #344154;
				font-size: 14px;
				font-weight: 600;
			}

			.el-table__body td,
			.el-table__body .default-cell {
				padding: 13px 0;
				border-color: #e3eaf1;
				background: #fff;
			}

			.el-table__body tr:hover > td {
				background: #f8fafc;
			}

			.el-checkbox__inner {
				width: 17px;
				height: 17px;
				border-radius: 3px;
			}
		}
	}

	.order-code-cell,
	.order-time-cell {
		display: flex;
		align-items: center;
		gap: 8px;
		color: #667485;
		white-space: nowrap;

		i {
			color: #94a2b3;
			font-size: 15px;
		}
	}

	.order-code-cell {
		color: #2c5f88;
		font-weight: 500;
	}

	.product-name-cell {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 6px;
		min-width: 0;

		strong {
			max-width: 100%;
			overflow: hidden;
			color: #26384d;
			font-size: 14px;
			font-weight: 600;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		span {
			padding: 2px 7px;
			border-radius: 3px;
			background: #f1f4f7;
			color: #758296;
			font-size: 11px;
			line-height: 18px;
		}
	}

	.invoice-amount-cell {
		display: flex;
		align-items: baseline;
		justify-content: flex-end;
		color: #d95534;

		span {
			margin-right: 3px;
			font-size: 13px;
		}

		strong {
			font-size: 18px;
			font-weight: 600;
		}
	}

	.invoice-footer {
		margin-top: 16px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		min-height: 64px;
		padding: 0 18px;
		border: 1px solid #e4ebf3;
		border-radius: 7px;
		background: #f8fafc;

		.selected-info {
			font-size: 14px;
			color: #606266;

			em {
				font-style: normal;
				color: #1877b9;
				font-weight: bold;
				margin: 0 5px;
			}

			.total-amount {
				font-size: 20px;
				color: #d95534;
				font-weight: bold;
				margin-left: 10px;
			}
		}

		.el-button {
			min-width: 132px;
			height: 42px;
			padding: 0 22px;
			border-radius: 4px;
			font-size: 15px;
		}
	}

	.invoice-dialog-title {
		display: flex;
		align-items: center;
		gap: 13px;
		min-height: 76px;
		padding-right: 42px;

		&__icon {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 42px;
			height: 42px;
			border-radius: 8px;
			background: #eaf4ff;
			color: #2476c9;
			font-size: 21px;
		}

		h4 {
			margin: 0;
			color: #273548;
			font-size: 20px;
			font-weight: 600;
			line-height: 28px;
		}

		p {
			display: flex;
			align-items: center;
			gap: 9px;
			margin: 3px 0 0;
			color: #8993a2;
			font-size: 13px;
			line-height: 20px;

			i {
				width: 1px;
				height: 12px;
				background: #d9e0e8;
			}

			strong {
				color: #d55535;
				font-weight: 600;
			}
		}
	}

	.invoice-dialog-content {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		padding: 0;
	}

	.invoice-title-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;

		h5 {
			margin: 0;
			color: #344154;
			font-size: 15px;
			font-weight: 600;
			line-height: 24px;
		}

		.el-button {
			padding: 5px 0;
			color: #3979b8;
		}
	}

	.invoice-title-form-item {
		margin-bottom: 16px;

		.el-select {
			width: 100%;
		}

		::v-deep {
			.el-input__inner {
				height: 46px;
				padding: 0 42px 0 15px;
				border-color: #d8e0e9;
				border-radius: 7px;
				background: #ffffff;
				color: #2f3d50;
				font-size: 14px;
				line-height: 46px;
				transition: border-color 0.2s ease, box-shadow 0.2s ease;
			}

			.el-input__inner:hover {
				border-color: #9bbcdc;
			}

			.el-input.is-focus .el-input__inner {
				border-color: #3a83c7;
				box-shadow: 0 0 0 3px rgba(58, 131, 199, 0.11);
			}

			.el-form-item__error {
				padding-top: 5px;
			}
		}
	}

	.title-option-name {
		float: left;
		max-width: calc(100% - 100px);
		overflow: hidden;
		color: #344154;
		font-weight: 500;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.title-option-type {
		float: right;
		margin-left: 24px;
		color: #8793a3;
		font-size: 12px;
	}

	.invoice-title-preview {
		overflow: hidden;
		margin-bottom: 16px;
		border: 1px solid #dfe6ee;
		border-radius: 8px;
		background: #ffffff;
	}

	.preview-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 48px;
		padding: 0 16px;
		border-bottom: 1px solid #e5eaf0;
		background: #f7f9fb;

		> div:first-child {
			display: flex;
			align-items: center;
			gap: 8px;
			color: #344154;
			font-size: 14px;
			font-weight: 600;

			i {
				color: #4d86bb;
				font-size: 16px;
			}
		}
	}

	.preview-tags {
		display: flex;
		gap: 6px;

		span {
			padding: 3px 8px;
			border: 1px solid #d4e2ef;
			border-radius: 4px;
			background: #edf5fb;
			color: #46759f;
			font-size: 11px;
			font-weight: normal;
			line-height: 16px;
		}
	}

	.preview-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding: 3px 16px 10px;
	}

	.preview-row {
		display: grid;
		grid-template-columns: 96px minmax(0, 1fr);
		min-height: 43px;
		border-bottom: 1px dashed #e7ebf0;

		&--wide {
			grid-column: 1 / -1;
		}

		span,
		strong {
			display: flex;
			align-items: center;
			padding: 9px 4px;
			line-height: 24px;
		}

		span {
			color: #8a95a4;
			font-weight: normal;
		}

		strong {
			min-width: 0;
			padding-right: 14px;
			color: #334155;
			font-weight: 500;
			word-break: break-all;
		}
	}

	.form-tips {
		display: flex;
		align-items: flex-start;
		gap: 9px;
		padding: 11px 13px;
		border: 1px solid #eee4cf;
		border-radius: 7px;
		background: #fffaf1;
		color: #7c7467;

		> i {
			flex-shrink: 0;
			margin-top: 3px;
			color: #c49238;
			font-size: 15px;
		}

		p {
			margin: 0;
			font-size: 12px;
			line-height: 20px;
		}
	}

	.form-button-group {
		display: flex;
		justify-content: flex-end;
		gap: 10px;
		margin: 22px -28px 0;
		padding: 17px 28px 19px;
		border-top: 1px solid #e7ebf0;
		background: #fbfcfd;

		.el-button + .el-button {
			margin-left: 0;
		}

		.form-button {
			min-width: 108px;
			height: 40px;
			padding: 0 20px;
			border-radius: 6px;
			font-size: 14px;
		}

		.form-button--default {
			border-color: #d5dce5;
			background: #ffffff;
			color: #5d6878;
		}

		.form-button--primary {
			border-color: #2879c6;
			background: #2879c6;
			box-shadow: 0 4px 10px rgba(40, 121, 198, 0.18);
		}
	}

	@media (max-width: 900px) {
		.invoice-selection-content {
			padding: 18px;
		}

		.invoice-order-panel {
			padding: 14px;
		}

		.invoice-panel-heading div > span {
			display: none;
		}

		.invoice-footer {
			padding: 12px 16px;
		}

		.invoice-title-preview {
			margin-left: 0;
		}

		.preview-grid {
			grid-template-columns: 1fr;
		}

		.preview-row {
			grid-column: 1;
			grid-template-columns: 88px minmax(0, 1fr);
		}

		.invoice-dialog-title p strong {
			display: none;
		}

		.form-button-group {
			margin-right: -18px;
			margin-left: -18px;
			padding-right: 18px;
			padding-left: 18px;
		}
	}

	// 重置表单样式
	.form-content {
		.form-item {
			::v-deep {
				.el-input {
					input {
						padding: 0 15px 0 15px;
					}
				}
			}
		}
	}
</style>

<style lang="scss">
	.invoice-apply-dialog {
		overflow: hidden;
		max-height: calc(100vh - 48px) !important;
		margin-top: 24px !important;
		border: 1px solid #d9e3ed;
		border-radius: 10px;
		background: #ffffff;
		box-shadow: 0 18px 48px rgba(35, 55, 80, 0.2);

		.el-dialog__header {
			min-height: 76px;
			padding: 0 28px;
			border-bottom: 1px solid #e5eaf0;
			background: #fbfcfe;
		}

		.el-dialog__headerbtn {
			top: 22px;
			right: 24px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
			border-radius: 6px;
			background: transparent;
			transition: background-color 0.2s ease;

			.el-dialog__close {
				color: #748093;
				font-size: 19px;
				font-weight: 600;
			}

			&:hover {
				background: #edf1f5;

				.el-dialog__close {
					color: #344154;
				}
			}
		}

		.el-dialog__body {
			display: flex;
			flex-direction: column;
			min-height: 0;
			padding: 22px 28px 0;
			overflow: auto;
			background: #ffffff;
		}
	}

	@media (max-width: 760px) {
		.invoice-apply-dialog {
			width: calc(100vw - 32px) !important;
			max-height: calc(100vh - 24px) !important;
			margin-top: 12px !important;

			.el-dialog__header {
				padding: 0 18px;
			}

			.el-dialog__headerbtn {
				right: 14px;
			}

			.el-dialog__body {
				padding: 18px 18px 0;
			}
		}
	}
</style>
